import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, Scale, Users, Brain, ExternalLink, CheckCircle } from 'lucide-react';

const MySolicitorPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 bg-gradient-to-b from-primary/5 to-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Shield className="w-4 h-4" />
                SRA Regulated Law Firm
              </div>
              
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
                MySolicitor.co.uk — Powered by BlueLion Law
              </h1>
              
              <h2 className="text-xl sm:text-2xl font-semibold text-accent mb-6">
                BlueLion Law is Proud to Power MySolicitor.co.uk
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto">
                A dedicated consumer platform built to help UK drivers reclaim hidden commission on mis-sold car finance
              </p>
              
              <div className="bg-card border border-border rounded-xl p-8 text-left max-w-3xl mx-auto">
                <p className="text-foreground leading-relaxed">
                  MySolicitor.co.uk is a consumer brand operated by BlueLion Law Limited, an SRA-regulated law firm authorised to provide legal services in England and Wales. Everything that happens on MySolicitor.co.uk — every claim assessed, every agreement located, every client represented — is handled directly by BlueLion Law Limited and its qualified legal team.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Brand Relationship Section */}
        <section className="py-20 lg:py-28 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Scale className="w-6 h-6 text-accent" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  The Relationship Between MySolicitor.co.uk and BlueLion Law
                </h2>
              </div>
              
              <div className="space-y-6 text-foreground leading-relaxed">
                <p>
                  MySolicitor.co.uk is one of the leading consumer platforms in the UK focused specifically on PCP and motor finance mis-selling claims. The platform was created to give consumers a simple, accessible way to understand their rights and start a claim — without legal jargon, without complexity, and without any upfront cost.
                </p>
                <p>
                  Behind MySolicitor.co.uk is BlueLion Law Limited — a fully regulated law firm with the expertise, regulatory authorisation, and proven track record to take PCP and motor finance claims all the way through to successful resolution. MySolicitor.co.uk is not a claims management company. It is not a lead generation service. It is a brand of BlueLion Law Limited, and every consumer who uses it is a client of BlueLion Law.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What MySolicitor.co.uk Offers Section */}
        <section className="py-20 lg:py-28 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-gold" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  What MySolicitor.co.uk Offers Consumers
                </h2>
              </div>
              
              <p className="text-foreground leading-relaxed mb-8">
                Through MySolicitor.co.uk, consumers across the UK can access BlueLion Law's full PCP and motor finance claims service, including:
              </p>
              
              <div className="grid gap-4">
                {[
                  {
                    icon: CheckCircle,
                    text: 'Eligibility checking using our agreement location technology, which can trace finance agreements back to 2007 using only a name, date of birth and address'
                  },
                  {
                    icon: CheckCircle,
                    text: 'Expert assessment of whether commission was properly disclosed on their agreement'
                  },
                  {
                    icon: CheckCircle,
                    text: "Full claims management handled entirely by BlueLion Law's legal team"
                  },
                  {
                    icon: CheckCircle,
                    text: 'No win, no fee representation throughout the entire claims process'
                  },
                  {
                    icon: Brain,
                    text: 'Access to the BlueLion Knowledge Brain — an AI-powered information system built specifically around PCP and motor finance claims'
                  }
                ].map((item, index) => (
                  <Card key={index} className="border-border">
                    <CardContent className="p-4 flex items-start gap-4">
                      <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <item.icon className="w-4 h-4 text-accent" />
                      </div>
                      <p className="text-foreground">{item.text}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Regulatory Information Section */}
        <section className="py-20 lg:py-28 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-accent" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  Regulatory Information
                </h2>
              </div>
              
              <Card className="border-border bg-card">
                <CardContent className="p-8">
                  <div className="space-y-4 text-foreground">
                    <p className="font-semibold text-lg">
                      MySolicitor.co.uk is a trading brand of BlueLion Law Limited.
                    </p>
                    <p>
                      BlueLion Law Limited is authorised and regulated by the Solicitors Regulation Authority.
                    </p>
                    <p className="font-semibold text-accent">
                      SRA Registration Number: 815166
                    </p>
                    <p>
                      BlueLion Law Limited is registered in England and Wales.
                    </p>
                    <p className="text-muted-foreground mt-6 pt-6 border-t border-border">
                      MySolicitor.co.uk operates exclusively under the regulatory oversight of the Solicitors Regulation Authority. All consumer data collected through MySolicitor.co.uk is processed and held by BlueLion Law Limited in accordance with applicable data protection legislation.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 lg:py-28 bg-primary text-primary-foreground">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">
                Visit MySolicitor.co.uk
              </h2>
              <p className="text-lg text-primary-foreground/80 mb-8 leading-relaxed">
                If you had a car on finance at any point between 2007 and 2024, you may be entitled to compensation. MySolicitor.co.uk makes it simple to find out.
              </p>
              <Button 
                size="lg" 
                className="bg-gold hover:bg-gold/90 text-primary font-semibold px-8 py-6 text-lg"
                asChild
              >
                <a href="https://www.mysolicitor.co.uk" target="_blank" rel="noopener noreferrer">
                  Visit MySolicitor.co.uk
                  <ExternalLink className="w-5 h-5 ml-2" />
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MySolicitorPage;
